import React from 'react'
import { withStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import { useSelector } from 'react-redux'
import CoreNavigation from 'src/components/core/core-tab'
import { CoreButton } from 'src/components/core'
import styles from './styles'
import {
  SidebarWidget,
  ModalWidget,
  DirectLinkWidget,
  EmbedWidget,
  WidgetViewHeader,
} from './components'
import {
  WIDGET_TYPES,
  INITIAL_WIDGET_OPTIONS,
  WIDGET_JS_TEMPLATE,
  WIDGET_HTML_TEMPLATES,
  DEFAULT_WIDGET_CONFIGS,
} from './components/constants'
import UserType from 'src/model/user-types'

const components = {
  [WIDGET_TYPES.SIDEBAR]: SidebarWidget,
  [WIDGET_TYPES.MODAL]: ModalWidget,
  [WIDGET_TYPES.DIRECT_LINK]: DirectLinkWidget,
  [WIDGET_TYPES.EMBED]: EmbedWidget,
}

const useWidgetOptions = (initialWidgetType = WIDGET_TYPES.SIDEBAR) => {
  const [widgetOptions, setWidgetOptions] = React.useState(
    INITIAL_WIDGET_OPTIONS.map(option => ({
      ...option,
      isActive: option.widgetType === initialWidgetType,
    })),
  )

  const handleWidgetSelect = widgetType => {
    setWidgetOptions(prevOptions =>
      prevOptions.map(option => ({
        ...option,
        isActive: option.widgetType === widgetType,
      })),
    )
  }

  const activeWidgetType = widgetOptions.find(option => option.isActive)
    ?.widgetType

  return {
    widgetOptions: widgetOptions.map(option => ({
      ...option,
      onClick: () => handleWidgetSelect(option.widgetType),
    })),
    handleWidgetSelect,
    activeWidgetType,
  }
}

const WidgetView = props => {
  const { classes } = props
  const me = useSelector(state => state.authentication.me)
  const userRoleId =
    me?.userRoleId ?? Number(window?.CONSTANTS?.USER_ROLE_ID)
  const userControl = UserType[userRoleId]
  // Embeddable widgets start at Growth.
  // Basic, AppSumo Tier 1/2/3, Essentials, Plus, Pro v2, Launch.
  const blockedRoleIds = new Set([1, 4, 5, 7, 27, 33, 34, 35])
  const hasWidgetAccess = Boolean(userRoleId) && !blockedRoleIds.has(userRoleId) &&
    !(
      userControl === 'Essentials' ||
      userControl === 'Plus' ||
      userControl === 'Pro v2' ||
      userControl === 'Launch' ||
      userControl === 'Basic'
    )

  const { widgetOptions, activeWidgetType } = useWidgetOptions()
  const ActiveComponent = components[activeWidgetType]
  const [widgetConfig, setWidgetConfig] = React.useState(null)
  const collection = useSelector(state => state.collection.collection)

  // Get the widget type key from the active widget type
  const getWidgetTypeKey = type => {
    switch (type) {
      case WIDGET_TYPES.SIDEBAR:
        return 'sidebar'
      case WIDGET_TYPES.MODAL:
        return 'modal'
      case WIDGET_TYPES.DIRECT_LINK:
        return 'direct'
      case WIDGET_TYPES.EMBED:
        return 'embed'
      default:
        return 'sidebar'
    }
  }

  const widgetTypeKey = getWidgetTypeKey(activeWidgetType)

  // Update config when widget type changes or collection changes
  React.useEffect(() => {
    if (collection) {
      const defaultConfigs = DEFAULT_WIDGET_CONFIGS(collection)
      setWidgetConfig(defaultConfigs[widgetTypeKey])
    }
  }, [widgetTypeKey, collection])

  const handleConfigChange = newConfig => {
    setWidgetConfig(newConfig)
  }

  const getDefaultConfig = () => {
    if (!collection) return null
    const defaultConfigs = DEFAULT_WIDGET_CONFIGS(collection)
    return defaultConfigs[widgetTypeKey]
  }

  const currentConfig = widgetConfig || getDefaultConfig()
  const jsCode = currentConfig
    ? WIDGET_JS_TEMPLATE(currentConfig, collection)
    : ''
  const htmlCode = currentConfig
    ? WIDGET_HTML_TEMPLATES[widgetTypeKey](currentConfig)
    : ''

  if (!hasWidgetAccess) {
    return (
      <div className={classes.root}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            padding: '40px 24px',
            borderRadius: '16px',
            border: '1px solid #E0E0E0',
            marginTop: '24px',
          }}
        >
          <Typography
            style={{ color: '#002E47', fontWeight: 500, fontSize: '18px' }}
          >
            Would you like to use widgets?
          </Typography>
          <Typography
            style={{ color: '#637381', fontSize: '14px', fontWeight: 400 }}
          >
            With a higher plan, you'll be able to deliver seamless experiences
            with widgets that work as sidebars, modals, direct links, or
            embedded blocks.
          </Typography>
          <CoreButton
            fullWidth
            onClick={() => {
              window.location.href = '/pricing'
            }}
          >
            See Plans & Upgrade
          </CoreButton>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          overflowX: 'auto',
          //hide scrollbar
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <CoreNavigation
          tabList={widgetOptions}
          fullWidth
          color="secondary"
          backgroundColor="secondary"
        />
      </div>
      {currentConfig && (
        <>
          <WidgetViewHeader
            widgetType={activeWidgetType}
            config={currentConfig}
          />

          <div className={classes.content}>
            {ActiveComponent && (
              <ActiveComponent
                jsCode={jsCode}
                htmlCode={htmlCode}
                config={currentConfig}
                onConfigChange={handleConfigChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default withStyles(styles)(WidgetView)
